import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rfiApi } from '../services/api'
import toast from 'react-hot-toast'
import { Download, Search } from 'lucide-react'

interface RigaMateriale {
  id: number
  tariffa_codice?: string
  descrizione: string
  unita_misura?: string
  quantita: number
  prezzo_unitario: number
  costo_totale: number
}
interface RigaManodopera {
  id: number
  tecnico_id?: number
  tariffa_codice?: string
  descrizione?: string
  n_ore: number
  prezzo_orario: number
  costo_totale: number
}
interface VoceManodopera {
  tariffa: string
  descrizione: string
  prezzo: number
}
interface Guerrato {
  id: number
  titolo?: string
  sito?: string
  n_richiesta?: string
  data_richiesta?: string
  redatto_da?: string
  redatto_data?: string
  ribasso_contrattuale_pct?: number
  spese_generali_pct?: number
  utile_impresa_pct?: number
  righe_materiali: RigaMateriale[]
  righe_manodopera: RigaManodopera[]
}
interface SuggerimentoListino {
  tariffa: string
  descrizione: string
  unita_misura?: string
  prezzo: number
  similarity: number
}

function fmt(n: number) {
  return n.toFixed(2)
}

function RigaMaterialeRow({ riga, onAbbinata }: { riga: RigaMateriale; onAbbinata: () => void }) {
  const [query, setQuery] = useState('')
  const [suggerimenti, setSuggerimenti] = useState<SuggerimentoListino[]>([])
  const [cercando, setCercando] = useState(false)

  const abbinaMut = useMutation({
    mutationFn: (s: SuggerimentoListino) => rfiApi.aggiornaRigaMateriale(riga.id, {
      tariffa_codice: s.tariffa, unita_misura: s.unita_misura, prezzo_unitario: s.prezzo,
    }),
    onSuccess: () => { toast.success('Voce di tariffa abbinata'); onAbbinata() },
    onError: () => toast.error('Errore abbinamento'),
  })

  const cerca = async () => {
    if (query.length < 2) return
    setCercando(true)
    try {
      const res = await rfiApi.cercaListino(query)
      setSuggerimenti(res.data as SuggerimentoListino[])
    } finally {
      setCercando(false)
    }
  }

  useEffect(() => {
    const t = setTimeout(() => { if (query.length >= 2) cerca() }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  return (
    <tr className="border-b">
      <td className="px-2 py-1 text-xs">{riga.tariffa_codice || <span className="text-red-500">da abbinare</span>}</td>
      <td className="px-2 py-1 text-xs">{riga.descrizione}</td>
      <td className="px-2 py-1 text-xs">{riga.unita_misura || '-'}</td>
      <td className="px-2 py-1 text-xs text-right">{riga.quantita}</td>
      <td className="px-2 py-1 text-xs text-right">{fmt(riga.prezzo_unitario)}</td>
      <td className="px-2 py-1 text-xs text-right font-medium">{fmt(riga.costo_totale)}</td>
      <td className="px-2 py-1">
        {!riga.tariffa_codice && (
          <div className="relative">
            <div className="flex gap-1">
              <input className="input text-xs py-1 w-40" placeholder="cerca nel listino..." value={query}
                onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && cerca()} />
              <button className="btn-ghost text-xs py-1" onClick={cerca} disabled={cercando}><Search size={12} /></button>
            </div>
            {suggerimenti.length > 0 && (
              <div className="absolute z-10 mt-1 w-72 card p-1 shadow-lg text-xs max-h-48 overflow-y-auto">
                {suggerimenti.map(s => (
                  <button key={s.tariffa} className="block w-full text-left px-2 py-1 hover:bg-slate-100 rounded"
                    onClick={() => { abbinaMut.mutate(s); setSuggerimenti([]) }}>
                    <div className="font-medium">{s.tariffa}</div>
                    <div className="text-slate-500">{s.descrizione} — €{fmt(s.prezzo)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}

function RigaManodoperaRow({ riga, voci, onSalvata }: { riga: RigaManodopera; voci: VoceManodopera[]; onSalvata: () => void }) {
  const [nOre, setNOre] = useState(riga.n_ore)
  const [prezzoOrario, setPrezzoOrario] = useState(riga.prezzo_orario)

  const aggiornaMut = useMutation({
    mutationFn: (data: { n_ore: number; prezzo_orario: number }) =>
      rfiApi.aggiornaRigaManodopera(riga.id, data),
    onSuccess: () => { toast.success('Riga manodopera aggiornata'); onSalvata() },
    onError: () => toast.error('Errore aggiornamento riga manodopera'),
  })

  const assegnaMut = useMutation({
    mutationFn: (tariffa_codice: string) => rfiApi.assegnaTariffaManodopera(riga.id, tariffa_codice),
    onSuccess: () => { toast.success('Tariffa assegnata (salvata anche in anagrafica tecnico)'); onSalvata() },
    onError: () => toast.error('Errore assegnazione tariffa'),
  })

  const nonMappata = !riga.tariffa_codice && !!riga.tecnico_id

  return (
    <tr className="border-b">
      <td className="px-2 py-1 text-xs">
        {riga.descrizione}
        {nonMappata && (
          <select
            className="select text-xs py-1 mt-1 w-full border-amber-400"
            defaultValue=""
            onChange={e => { if (e.target.value) assegnaMut.mutate(e.target.value) }}
          >
            <option value="" disabled>⚠ assegna tariffa RFI...</option>
            {voci.map(v => (
              <option key={v.tariffa} value={v.tariffa}>{v.descrizione} ({v.prezzo.toFixed(2)} €/h)</option>
            ))}
          </select>
        )}
      </td>
      <td className="px-2 py-1 text-xs text-right">
        <input type="number" className="input text-xs py-1 w-20 text-right" value={nOre}
          onChange={e => setNOre(Number(e.target.value))}
          onBlur={() => { if (nOre !== riga.n_ore) aggiornaMut.mutate({ n_ore: nOre, prezzo_orario: prezzoOrario }) }} />
      </td>
      <td className="px-2 py-1 text-xs text-right">
        <input type="number" className="input text-xs py-1 w-20 text-right" value={prezzoOrario}
          onChange={e => setPrezzoOrario(Number(e.target.value))}
          onBlur={() => { if (prezzoOrario !== riga.prezzo_orario) aggiornaMut.mutate({ n_ore: nOre, prezzo_orario: prezzoOrario }) }} />
      </td>
      <td className="px-2 py-1 text-xs text-right font-medium">{fmt(riga.costo_totale)}</td>
    </tr>
  )
}

interface HeaderForm {
  titolo: string
  sito: string
  n_richiesta: string
  data_richiesta: string
  redatto_da: string
  redatto_data: string
  ribasso_contrattuale_pct: number
  spese_generali_pct: number
  utile_impresa_pct: number
}

function headerFormFromGuerrato(guerrato: Guerrato): HeaderForm {
  return {
    titolo: guerrato.titolo || '',
    sito: guerrato.sito || '',
    n_richiesta: guerrato.n_richiesta || '',
    data_richiesta: guerrato.data_richiesta || '',
    redatto_da: guerrato.redatto_da || '',
    redatto_data: guerrato.redatto_data || '',
    ribasso_contrattuale_pct: guerrato.ribasso_contrattuale_pct ?? 0,
    spese_generali_pct: guerrato.spese_generali_pct ?? 0,
    utile_impresa_pct: guerrato.utile_impresa_pct ?? 0,
  }
}

function HeaderCard({ guerrato, id }: { guerrato: Guerrato; id: string | undefined }) {
  const qc = useQueryClient()
  const [form, setForm] = useState<HeaderForm>(headerFormFromGuerrato(guerrato))

  useEffect(() => {
    setForm(headerFormFromGuerrato(guerrato))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guerrato.id])

  const salvaMut = useMutation({
    mutationFn: () => rfiApi.aggiornaGuerratoHeader(guerrato.id, {
      titolo: form.titolo || null,
      sito: form.sito || null,
      n_richiesta: form.n_richiesta || null,
      data_richiesta: form.data_richiesta || null,
      redatto_da: form.redatto_da || null,
      redatto_data: form.redatto_data || null,
      ribasso_contrattuale_pct: form.ribasso_contrattuale_pct,
      spese_generali_pct: form.spese_generali_pct,
      utile_impresa_pct: form.utile_impresa_pct,
    }),
    onSuccess: () => {
      toast.success('Intestazione salvata')
      qc.invalidateQueries({ queryKey: ['rfi-guerrato', id] })
    },
    onError: () => toast.error('Errore salvataggio intestazione'),
  })

  const campo = (label: string, key: keyof HeaderForm, type: string = 'text', step?: string) => (
    <div>
      <label className="label">{label}</label>
      <input
        type={type}
        step={step}
        className="input w-full"
        value={form[key] as string | number}
        onChange={e => setForm(f => ({
          ...f,
          [key]: type === 'number' ? Number(e.target.value) : e.target.value,
        }))}
      />
    </div>
  )

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-medium">Intestazione</h2>
        <button className="btn-primary text-sm" disabled={salvaMut.isPending} onClick={() => salvaMut.mutate()}>
          {salvaMut.isPending ? 'Salvataggio...' : 'Salva'}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {campo('Titolo', 'titolo')}
        {campo('Sito', 'sito')}
        {campo('N. Richiesta', 'n_richiesta')}
        {campo('Data Richiesta', 'data_richiesta', 'date')}
        {campo('Redatto da', 'redatto_da')}
        {campo('Redatto il', 'redatto_data', 'date')}
        {campo('Ribasso contrattuale (%)', 'ribasso_contrattuale_pct', 'number', '0.001')}
        {campo('Spese generali (%)', 'spese_generali_pct', 'number', '0.001')}
        {campo('Utile impresa (%)', 'utile_impresa_pct', 'number', '0.001')}
      </div>
    </div>
  )
}

export default function RfiRapportinoGuerratoEditor() {
  const { id } = useParams()
  const qc = useQueryClient()

  const { data: guerrato, isLoading } = useQuery({
    queryKey: ['rfi-guerrato', id],
    queryFn: () => rfiApi.getGuerrato(Number(id)).then(r => r.data as Guerrato),
    enabled: !!id,
  })
  const { data: vociManodopera = [] } = useQuery({
    queryKey: ['rfi-listino-manodopera'],
    queryFn: () => rfiApi.listinoManodopera().then(r => r.data as VoceManodopera[]),
    staleTime: 5 * 60_000,
  })

  const downloadPdf = async () => {
    try {
      const res = await rfiApi.downloadPdfGuerrato(Number(id))
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url; a.download = `rapportino_guerrato_${id}.pdf`; a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Errore generazione PDF')
    }
  }

  if (isLoading || !guerrato) return <div className="text-center py-16 text-slate-400">Caricamento...</div>

  const totaleMateriali = guerrato.righe_materiali.reduce((s, r) => s + r.costo_totale, 0)
  const totaleManodopera = guerrato.righe_manodopera.reduce((s, r) => s + r.costo_totale, 0)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{guerrato.titolo || 'Rapportino Guerrato'}</h1>
        <button className="btn-primary flex items-center gap-1.5" onClick={downloadPdf}>
          <Download size={16} /> Genera PDF
        </button>
      </div>

      <HeaderCard guerrato={guerrato} id={id} />

      <div className="card p-4">
        <h2 className="font-medium mb-2">Sezione A — Materiali</h2>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b">
            <th className="text-left px-2 py-1 text-xs text-slate-500">Voce di Tariffa</th>
            <th className="text-left px-2 py-1 text-xs text-slate-500">Descrizione</th>
            <th className="text-left px-2 py-1 text-xs text-slate-500">UM</th>
            <th className="text-right px-2 py-1 text-xs text-slate-500">Quantità</th>
            <th className="text-right px-2 py-1 text-xs text-slate-500">Prezzo Unit.</th>
            <th className="text-right px-2 py-1 text-xs text-slate-500">Costo Totale</th>
            <th className="px-2 py-1"></th>
          </tr></thead>
          <tbody>
            {guerrato.righe_materiali.map(r => (
              <RigaMaterialeRow key={r.id} riga={r} onAbbinata={() => qc.invalidateQueries({ queryKey: ['rfi-guerrato', id] })} />
            ))}
          </tbody>
        </table>
        <div className="text-right text-sm font-semibold mt-2">Totale parziale: € {fmt(totaleMateriali)}</div>
      </div>

      <div className="card p-4">
        <h2 className="font-medium mb-2">Sezione E — Manodopera</h2>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b">
            <th className="text-left px-2 py-1 text-xs text-slate-500">Descrizione</th>
            <th className="text-right px-2 py-1 text-xs text-slate-500">N.Ore</th>
            <th className="text-right px-2 py-1 text-xs text-slate-500">Prezzo</th>
            <th className="text-right px-2 py-1 text-xs text-slate-500">Costo Totale</th>
          </tr></thead>
          <tbody>
            {guerrato.righe_manodopera.map(r => (
              <RigaManodoperaRow key={r.id} riga={r} voci={vociManodopera} onSalvata={() => qc.invalidateQueries({ queryKey: ['rfi-guerrato', id] })} />
            ))}
          </tbody>
        </table>
        <div className="text-right text-sm font-semibold mt-2">Importo manodopera: € {fmt(totaleManodopera)}</div>
      </div>
    </div>
  )
}
